import { AuthenticatedApiRequest } from '@goldspotdiscoveries/auth';
import { parseQueryParams } from '../helpers/parse-query-params';
import { LogsApiLogObject } from '@goldspotdiscoveries/atoms';
import {
  GetLogDataBulkResponse,
  GetLogDataResponse,
  LogsApiDataItem,
  LogsApiBulkData,
} from '@goldspotdiscoveries/types';

interface GetLogParams {
  company: string;
  projectId: string;
  logId: string;
}

export const getLog = ({ company, projectId, logId }: GetLogParams) =>
  AuthenticatedApiRequest<LogsApiLogObject>({
    method: 'get',
    apiName: 'Projects',
    path: `/${company}/${projectId}/logs/${logId}`,
  });

interface GetLogsParams {
  company: string;
  projectId: string;
  queryParams?: {
    limit?: number;
    sortBy?: string;
    offset_token?: string;
    type?: string;
  };
}

interface GetLogsResponse {
  items: LogsApiLogObject[];
  next_offset_token?: string;
}

export const getLogs = ({ company, projectId, queryParams }: GetLogsParams) => {
  const validKeys: string[] = ['limit', 'sortBy', 'offset_token', 'type'];
  const addedQuery = { limit: 1000, ...queryParams };
  const queryString: string = parseQueryParams(validKeys, addedQuery);
  return AuthenticatedApiRequest<GetLogsResponse>({
    method: 'get',
    apiName: 'Projects',
    path: `/${company}/${projectId}/logs${queryString}`,
  });
};

export interface GetLogDataItemsParams {
  company: string;
  projectId: string;
  logId: string;
  queryParams?: {
    /**
     * Properties should be an array of properties from `LogsApiLogObject`
     * schema with no quotes around each column
     *
     * @example `'[depth_from,depth_to,drillhole_id]'`
     */
    properties?: string;
    drillhole_id?: string;
    sortBy?: string;
    /**
     * If provided helps pagination by defining from where the next page starts. It will be a project ID
     *
     * @example 'the-x-project'
     */
    offset_token?: string;
    /**
     * Amount of items to be returned.
     *
     * @example '6'
     */
    limit?: string;
    bulk?: 'true';
  };
}

export const getLogDataItems = <T = GetLogDataResponse>({
  company,
  projectId,
  logId,
  queryParams,
}: GetLogDataItemsParams): Promise<T> => {
  const validKeys: string[] = [
    'properties',
    'drillhole_id',
    'limit',
    'sortBy',
    'offset_token',
    'bulk',
  ];
  const addedParams = { limit: 1000, ...queryParams };
  return AuthenticatedApiRequest<T>({
    method: 'get',
    apiName: 'Projects',
    path: `/${company}/${projectId}/logs/${logId}/data${parseQueryParams(
      validKeys,
      addedParams,
    )}`,
  });
};

/**
 * Fetch all log data items (recursively fetches all queried items using offset tokens)
 */
export const getAllLogDataItems = async ({
  company,
  projectId,
  logId,
  queryParams,
}: GetLogDataItemsParams): Promise<LogsApiDataItem[]> => {
  // Accumulator for `getBatch`
  const logDataItems: LogsApiDataItem[] = [];
  /**
   * Recursively fetch all log data items
   */
  const getBatch = async (params: GetLogDataItemsParams): Promise<void> => {
    return await getLogDataItems(params).then(
      async (response: GetLogDataResponse) => {
        const { items, next_offset_token } = response;
        items && logDataItems.push(...(items as unknown as LogsApiDataItem[]));
        if (next_offset_token) {
          const newParams = {
            ...params,
            queryParams: {
              ...params.queryParams,
              offset_token: next_offset_token,
            },
          };
          await getBatch(newParams);
        } else {
          return;
        }
      },
    );
  };
  await getBatch({ company, projectId, logId, queryParams });
  return logDataItems;
};

/**
 * Fetches all pages of log data items in bulk and combines each column in page order.
 */
export const getAllBulkLogDataItems = async ({
  company,
  projectId,
  logId,
  queryParams,
}: GetLogDataItemsParams): Promise<LogsApiBulkData> => {
  // Accumulator for the bulk data
  const bulkData: Partial<LogsApiBulkData> = {};
  let offsetToken: string | undefined;

  do {
    const response = await getLogDataItems<GetLogDataBulkResponse>({
      company,
      projectId,
      logId,
      queryParams: {
        ...queryParams,
        bulk: 'true',
        ...(offsetToken ? { offset_token: offsetToken } : {}),
      },
    });

    // Merge the current page of data into the accumulator
    for (const [column, values] of Object.entries(response.data ?? {})) {
      const columnValues = (bulkData[column] ??= []);
      for (const value of values) {
        columnValues.push(value);
      }
    }

    offsetToken = response.next_offset_token;
  } while (offsetToken);

  return bulkData as LogsApiBulkData;
};

export const putLog = (
  company: string,
  project: string,
  request: any,
): Promise<LogsApiLogObject> => {
  return new Promise((resolve, reject) => {
    AuthenticatedApiRequest({
      method: 'put',
      apiName: 'Projects',
      path: `/${company}/${project}/logs`,
      postData: {
        body: request,
      },
    })
      .then((value) => {
        const logObject: LogsApiLogObject = value as any;
        resolve(logObject);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const updateLog = (
  company: string,
  project: string,
  logId: string,
  body: any,
): Promise<LogsApiLogObject> => {
  return new Promise((resolve, reject) => {
    AuthenticatedApiRequest({
      method: 'post',
      apiName: 'Projects',
      path: `/${company}/${project}/logs/${logId}`,
      postData: { body },
    })
      .then((response) => {
        resolve(response as LogsApiLogObject);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const deleteLog = (
  company: string,
  projectId: string,
  logId: string,
): Promise<{ message: string }> => {
  return new Promise((resolve, reject) => {
    AuthenticatedApiRequest<{ message: string }>({
      method: 'delete',
      apiName: 'Projects',
      path: `/${company}/${projectId}/logs/${logId}`,
    })
      .then((result) => {
        resolve(result);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const deleteLogDataItems = async (
  company: string,
  projectId: string,
  logId: string,
  queryParams?: Record<string, string>,
): Promise<{ message: string }> => {
  const validKeys: string[] = ['drillhole_id'];
  return await AuthenticatedApiRequest<{ message: string }>({
    method: 'delete',
    apiName: 'Projects',
    path: `/${company}/${projectId}/logs/${logId}/data${parseQueryParams(
      validKeys,
      queryParams,
    )}`,
  });
};

export const putLogDataItems = async (
  company: string,
  projectId: string,
  logId: string,
  body: Record<string, any>,
  queryParams?: Record<string, string>,
): Promise<{ message: string }> => {
  const validKeys: string[] = ['bulk'];
  return await AuthenticatedApiRequest<{ message: string }>({
    method: 'put',
    apiName: 'Projects',
    path: `/${company}/${projectId}/logs/${logId}/data${parseQueryParams(
      validKeys,
      queryParams,
    )}`,
    postData: {
      body,
    },
  });
};
